import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import '../styles/onboarding.css';

/* ============================================================
   ACTIVE STEP TRACKER (IntersectionObserver)
   ============================================================ */
function useActiveStep(count) {
  const [active, setActive] = useState(0);
  const stepRefs = useRef(Array(count).fill(null));

  useEffect(() => {
    const observers = stepRefs.current.map((el, i) => {
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(i); },
        { threshold: 0.35 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach(o => o?.disconnect());
  }, [count]);

  return { active, stepRefs };
}

/* ============================================================
   STEP TRACKER SIDEBAR
   ============================================================ */
function StepTracker({ active, steps }) {
  return (
    <div className="ob-tracker" aria-hidden="true">
      <div className="ob-tracker__inner">
        {steps.map((step, i) => (
          <div key={i} className={`ob-tracker__item ${active === i ? 'ob-tracker__item--active' : ''} ${active > i ? 'ob-tracker__item--done' : ''}`}>
            <div className="ob-tracker__dot">
              {active > i ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              ) : (
                <span>{String(i + 1).padStart(2, '0')}</span>
              )}
            </div>
            {i < steps.length - 1 && <div className="ob-tracker__line" />}
            <div className="ob-tracker__label">{step.trackerLabel}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   MOCKUP: CALENDAR (Step 1 — Book Appointment)
   ============================================================ */
function CalendarMockup() {
  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const dates = [null, null, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];
  const available = [5, 8, 12, 15, 19, 22, 26];

  return (
    <div className="ob-mockup ob-mockup--calendar">
      <div className="ob-mockup__header">
        <div className="ob-mockup__title-row">
          <div>
            <div className="ob-mockup__label">Book Your Presentation</div>
            <div className="ob-mockup__sublabel">30-minute free call</div>
          </div>
          <div className="ob-mockup__badge">Free</div>
        </div>
      </div>

      <div className="ob-calendar">
        <div className="ob-calendar__nav">
          <span>‹</span>
          <strong>July 2026</strong>
          <span>›</span>
        </div>
        <div className="ob-calendar__grid">
          {days.map((d, i) => (
            <div key={i} className="ob-calendar__day-hdr">{d}</div>
          ))}
          {dates.map((d, i) => (
            <div
              key={i}
              className={`ob-calendar__cell ${d === 16 ? 'ob-calendar__cell--selected' : available.includes(d) ? 'ob-calendar__cell--available' : ''}`}
            >
              {d || ''}
            </div>
          ))}
        </div>
      </div>

      <div className="ob-mockup__confirm">
        <div className="ob-mockup__confirm-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <div>
          <div className="ob-mockup__confirm-title">Thursday, July 16 · 2:00 PM</div>
          <div className="ob-mockup__confirm-sub">Confirmation sent to your email</div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MOCKUP: MEETING (Step 2 — Team Discussion)
   ============================================================ */
function MeetingMockup() {
  const AGENDA = [
    'Your business goals & workflows',
    'AI personality, tone & voice',
    'Channels to connect (call, chat, WhatsApp)',
    'Knowledge base & FAQs setup',
    'Go-live timeline',
  ];

  return (
    <div className="ob-mockup ob-mockup--meeting">
      <div className="ob-mockup__header">
        <div className="ob-mockup__title-row">
          <div>
            <div className="ob-mockup__label">Strategy Call</div>
            <div className="ob-mockup__sublabel">With your Klaivox team</div>
          </div>
          <div className="ob-mockup__duration">~45 min</div>
        </div>
      </div>

      <div className="ob-meeting__avatars">
        {['K', 'Y', 'A'].map((letter, i) => (
          <div key={i} className={`ob-meeting__avatar ob-meeting__avatar--${i}`}>{letter}</div>
        ))}
        <div className="ob-meeting__online">
          <div className="ob-meeting__online-dot" />
          Live on Google Meet
        </div>
      </div>

      <div className="ob-meeting__agenda-label">Meeting Agenda</div>
      <ul className="ob-meeting__agenda">
        {AGENDA.map((item, i) => (
          <li key={i} className="ob-meeting__agenda-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ============================================================
   MOCKUP: PAYMENT (Step 3 — Choose Plan & Pay)
   ============================================================ */
function PaymentMockup() {
  return (
    <div className="ob-mockup ob-mockup--payment">
      <div className="ob-mockup__header">
        <div className="ob-mockup__label">Order Summary</div>
        <div className="ob-mockup__sublabel">Secure checkout · Cancel anytime</div>
      </div>

      <div className="ob-payment__rows">
        <div className="ob-payment__row">
          <span>Klaivox Growth Plan</span>
          <span className="ob-payment__price">$99 CAD<span>/mo</span></span>
        </div>
        <div className="ob-payment__row ob-payment__row--muted">
          <span>Onboarding & Setup</span>
          <span className="ob-payment__free">Free</span>
        </div>
        <div className="ob-payment__row ob-payment__row--muted">
          <span>AI Voice Agent (1 included)</span>
          <span>✓</span>
        </div>
        <div className="ob-payment__row ob-payment__row--muted">
          <span>Custom Branding & Domain</span>
          <span>✓</span>
        </div>
        <div className="ob-payment__row ob-payment__row--muted">
          <span>All Channels Included</span>
          <span>✓</span>
        </div>
      </div>

      <div className="ob-payment__divider" />

      <div className="ob-payment__total">
        <span>Total Today</span>
        <span className="ob-payment__total-amount">$99 CAD / month</span>
      </div>

      <button className="ob-payment__btn" aria-label="Checkout">
        Secure Checkout →
      </button>

      <div className="ob-payment__trust">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        256-bit SSL · Powered by Stripe
      </div>
    </div>
  );
}

/* ============================================================
   MOCKUP: SETUP TERMINAL (Step 4 — 24h AI Build)
   ============================================================ */
const SETUP_TASKS = [
  { text: 'Business profile analyzed', delay: 0 },
  { text: 'Knowledge base configured', delay: 0.5 },
  { text: 'Brand colors & logo applied', delay: 1.0 },
  { text: 'AI trained on your tone & voice', delay: 1.5 },
  { text: 'Voice agent connected', delay: 2.0 },
  { text: 'Channels activated', delay: 2.5 },
];

function SetupMockup() {
  return (
    <div className="ob-mockup ob-mockup--setup">
      <div className="ob-mockup__header">
        <div className="ob-mockup__title-row">
          <div>
            <div className="ob-mockup__label">Building Your AI</div>
            <div className="ob-mockup__sublabel">Usually ready in 3–8 hours</div>
          </div>
          <div className="ob-setup__spinner" aria-hidden="true" />
        </div>
      </div>

      <div className="ob-setup__tasks">
        {SETUP_TASKS.map((task, i) => (
          <div
            key={i}
            className="ob-setup__task"
            style={{ animationDelay: `${task.delay}s` }}
          >
            <div className="ob-setup__task-icon">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <span>{task.text}</span>
          </div>
        ))}
      </div>

      <div className="ob-setup__progress-wrap">
        <div className="ob-setup__progress-bar" />
      </div>
      <div className="ob-setup__progress-label">
        <span>Progress</span>
        <span className="ob-setup__pct">89%</span>
      </div>
    </div>
  );
}

/* ============================================================
   MOCKUP: DASHBOARD (Step 5 — Live Access)
   ============================================================ */
function DashboardMockup() {
  const CONVOS = [
    { init: 'J', text: 'Just booked Thursday at 2pm — thanks!', time: '2m ago', dot: '#4ade80' },
    { init: 'M', text: 'Can you send me the pricing info?', time: '11m ago', dot: '#4F8EF7' },
    { init: 'S', text: 'Is there availability this weekend?', time: '34m ago', dot: '#6366f1' },
  ];

  return (
    <div className="ob-mockup ob-mockup--dashboard">
      <div className="ob-mockup__header">
        <div className="ob-mockup__title-row">
          <div>
            <div className="ob-mockup__label">Your Dashboard</div>
            <div className="ob-mockup__sublabel">AI is live & running</div>
          </div>
          <div className="ob-dashboard__status">
            <div className="ob-dashboard__status-dot" />
            Active
          </div>
        </div>
      </div>

      <div className="ob-dashboard__stats">
        {[
          { value: '247', label: 'Messages', color: '#4F8EF7' },
          { value: '98%', label: 'Response Rate', color: '#4ade80' },
          { value: '18', label: 'Bookings', color: '#6366f1' },
        ].map((s, i) => (
          <div key={i} className="ob-dashboard__stat">
            <div className="ob-dashboard__stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="ob-dashboard__stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="ob-dashboard__section-label">Recent Conversations</div>
      <div className="ob-dashboard__convos">
        {CONVOS.map((c, i) => (
          <div key={i} className="ob-dashboard__convo">
            <div className="ob-dashboard__convo-avatar" style={{ background: c.dot }}>{c.init}</div>
            <div className="ob-dashboard__convo-body">
              <div className="ob-dashboard__convo-text">{c.text}</div>
              <div className="ob-dashboard__convo-time">{c.time}</div>
            </div>
            <div className="ob-dashboard__convo-dot" style={{ background: c.dot }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   STEP DATA
   ============================================================ */
const STEPS = [
  {
    num: '01',
    trackerLabel: 'Book Call',
    title: 'Book Your Presentation',
    lead: 'Schedule a free 30-minute call with our team.',
    body: 'Choose a time that works for you. We\'ll walk you through exactly what Klaivox can do for your business, answer every question you have, and show you a live demo of the AI in action — tailored to your industry.',
    bullets: [
      'No commitment, no sales pressure',
      'Live demo of a real AI assistant',
      'Immediate answers to your questions',
    ],
    cta: { label: 'Book Your Free Demo →', to: '/contact' },
    Mockup: CalendarMockup,
    reverse: false,
  },
  {
    num: '02',
    trackerLabel: 'Team Meeting',
    title: 'Meet with Our Team',
    lead: 'A strategy session built around your business.',
    body: 'Once you\'re ready to move forward, we schedule a dedicated onboarding call. Our team will map out everything your AI assistant needs to know — your services, your tone, how you want leads handled, and which channels to activate.',
    bullets: [
      'We learn your business inside out',
      'Define the AI\'s personality & responses',
      'Set up your channels and integrations',
    ],
    cta: null,
    Mockup: MeetingMockup,
    reverse: true,
  },
  {
    num: '03',
    trackerLabel: 'Payment',
    title: 'Choose Your Plan',
    lead: 'One flat monthly fee. No surprises.',
    body: 'Pick the plan that fits your business size and goals. There are no per-message charges, no setup fee, and no long-term contracts — white-glove onboarding is included. You can upgrade, downgrade, or cancel anytime. Everything is handled through our secure Stripe checkout.',
    bullets: [
      'Plans from $29 CAD/month',
      'No setup fee · Cancel anytime',
      'All channels and integrations included',
    ],
    cta: { label: 'See All Plans →', to: '/pricing' },
    Mockup: PaymentMockup,
    reverse: false,
  },
  {
    num: '04',
    trackerLabel: 'AI Setup',
    title: 'We Build Your AI',
    lead: 'Your dedicated assistant goes live within 24 hours.',
    body: 'After payment, our dev team takes over completely. We configure your AI\'s knowledge base, apply your branding, connect your channels, and run thorough quality tests — all without you needing to lift a finger. Most setups are complete in 3–8 hours.',
    bullets: [
      'Full setup by our team — no tech skills needed',
      'Your brand, your logo, your domain',
      'Quality-tested before going live',
    ],
    cta: null,
    Mockup: SetupMockup,
    reverse: true,
  },
  {
    num: '05',
    trackerLabel: 'Dashboard',
    title: 'Access Your Dashboard',
    lead: 'Your AI is live. Your dashboard is waiting.',
    body: 'You\'ll receive login credentials to your personalized Klaivox dashboard where you can monitor conversations in real time, view booking stats, update your AI\'s knowledge, and manage all your settings. Your AI starts handling customers the moment it goes live.',
    bullets: [
      'Real-time conversation monitoring',
      'Booking and lead stats at a glance',
      'Update AI knowledge anytime',
    ],
    cta: { label: 'Get Started Today →', to: '/contact' },
    Mockup: DashboardMockup,
    reverse: false,
  },
];

/* ============================================================
   PAGE
   ============================================================ */
export default function Onboarding() {
  const { active, stepRefs } = useActiveStep(STEPS.length);

  return (
    <main className="onboarding-page">

      {/* ===== HERO ===== */}
      <section className="ob-hero" aria-label="Onboarding overview">
        <div className="ob-hero__blobs" aria-hidden="true">
          <div className="ob-hero__blob ob-hero__blob--1" />
          <div className="ob-hero__blob ob-hero__blob--2" />
        </div>
        <div className="ob-hero__inner">
          <AnimatedSection>
            <span className="section-label">Getting Started</span>
            <h1 className="ob-hero__title">
              Your Klaivox Journey
              <span className="ob-hero__title-line2">Start to finish, step by step.</span>
            </h1>
            <p className="ob-hero__sub">
              From your first conversation with us to a live AI assistant handling your customers — here's exactly what happens at every stage of the process.
            </p>
            <div className="ob-hero__steps-overview">
              {STEPS.map((s, i) => (
                <div key={i} className="ob-hero__step-pill">
                  <span className="ob-hero__step-num">{s.num}</span>
                  <span>{s.trackerLabel}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ===== STEPS ===== */}
      <section className="ob-steps-section" aria-label="Onboarding steps">
        <div className="ob-steps-layout">

          {/* Sticky sidebar tracker */}
          <StepTracker active={active} steps={STEPS} />

          {/* Step sections */}
          <div className="ob-steps-list">
            {STEPS.map((step, i) => (
              <div
                key={i}
                className="ob-step"
                ref={el => { stepRefs.current[i] = el; }}
              >
                <AnimatedSection>
                  <div className={`ob-step__content ${step.reverse ? 'ob-step__content--reverse' : ''}`}>

                    {/* Text column */}
                    <div className="ob-step__text-col">
                      <div className="ob-step__num-badge">{step.num}</div>
                      <p className="ob-step__of">Step {i + 1} of {STEPS.length}</p>
                      <h2 className="ob-step__title">{step.title}</h2>
                      <p className="ob-step__lead">{step.lead}</p>
                      <p className="ob-step__body">{step.body}</p>
                      <ul className="ob-step__bullets">
                        {step.bullets.map((b, j) => (
                          <li key={j}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            {b}
                          </li>
                        ))}
                      </ul>
                      {step.cta && (
                        <Link to={step.cta.to} className="btn-primary ob-step__cta">
                          {step.cta.label}
                        </Link>
                      )}
                    </div>

                    {/* Graphic column */}
                    <div className="ob-step__graphic-col">
                      <step.Mockup />
                    </div>

                  </div>
                </AnimatedSection>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="ob-final-cta" aria-label="Get started">
        <div className="ob-final-cta__blob" aria-hidden="true" />
        <AnimatedSection>
          <div className="ob-final-cta__inner">
            <span className="section-label">Ready to Begin?</span>
            <h2>Book Your Free 30-Minute Demo</h2>
            <p>See your business running on AI — live, in under half an hour. No commitment required.</p>
            <div className="ob-final-cta__actions">
              <Link to="/contact" className="btn-primary">
                Book My Free Demo →
              </Link>
              <Link to="/pricing" className="btn-secondary">
                View Pricing
              </Link>
            </div>
            <p className="ob-final-cta__trust">
              No long-term contracts · Your brand, not ours · Live in under 24 hours
            </p>
          </div>
        </AnimatedSection>
      </section>

    </main>
  );
}
